/**
 * Interactive 24h-load-history chart for /admin/servers/:id (OCP-105,
 * follow-up to OCP-78). Replaces the OCP-78-interim SparklineTable —
 * which we keep alive on the page as a "View as table" toggle for
 * operators who want exact tabular numbers + the existing
 * Sparkline-bar muscle memory.
 *
 * Three small Recharts LineCharts stacked vertically (Tenants /
 * RAM-MB / CPU-%) with shared X-axis + tooltip + time-range filter.
 * Three separate charts beats one chart with two Y-axes — the
 * units (count / MB / %) don't share a sensible scale.
 */
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fmtDateTime, fmtTime } from '../lib/format-time';

export interface HistoryPoint {
  bucket: string;
  tenant_count: number;
  ram_used_mb_sum: number;
  cpu_pct_avg: number;
}

type RangeOption = '6h' | '12h' | '24h';

const RANGE_HOURS: Record<RangeOption, number> = { '6h': 6, '12h': 12, '24h': 24 };

export function LoadHistoryChart({ rows }: { rows: HistoryPoint[] }) {
  const [range, setRange] = useState<RangeOption>('24h');

  const filtered = useMemo(() => {
    const cutoff = Date.now() - RANGE_HOURS[range] * 3_600_000;
    return rows
      .filter((r) => new Date(r.bucket).getTime() >= cutoff)
      .map((r) => ({ ...r, bucketMs: new Date(r.bucket).getTime() }))
      .sort((a, b) => a.bucketMs - b.bucketMs);
  }, [rows, range]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {filtered.length} bucket{filtered.length === 1 ? '' : 's'} in last {range}
        </span>
        <div className="flex gap-1">
          {(Object.keys(RANGE_HOURS) as RangeOption[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`rounded border px-2 py-0.5 text-xs ${
                range === r
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/60'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500">
          No telemetry in the last {range}. Try a wider range.
        </p>
      ) : (
        <div className="space-y-4">
          <ChartTile
            title="Tenants"
            data={filtered}
            dataKey="tenant_count"
            stroke="#8b5cf6"
            unit=""
          />
          <ChartTile
            title="RAM used (MB sum)"
            data={filtered}
            dataKey="ram_used_mb_sum"
            stroke="#3b82f6"
            unit=" MB"
          />
          <ChartTile
            title="CPU % (avg)"
            data={filtered}
            dataKey="cpu_pct_avg"
            stroke="#10b981"
            unit="%"
          />
        </div>
      )}
    </div>
  );
}

interface ChartDatum extends HistoryPoint {
  bucketMs: number;
}

function ChartTile({
  title,
  data,
  dataKey,
  stroke,
  unit,
}: {
  title: string;
  data: ChartDatum[];
  dataKey: keyof Pick<HistoryPoint, 'tenant_count' | 'ram_used_mb_sum' | 'cpu_pct_avg'>;
  stroke: string;
  unit: string;
}) {
  return (
    <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
      <div className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">{title}</div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#f5f5f5" vertical={false} />
          <XAxis
            dataKey="bucketMs"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => formatHour(Number(v))}
            tick={{ fontSize: 10, fill: '#737373' }}
            stroke="#e5e5e5"
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#737373' }}
            stroke="#e5e5e5"
            width={40}
            tickFormatter={(v) => {
              const n = Number(v);
              return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n));
            }}
          />
          <Tooltip
            labelFormatter={(v) => fmtDateTime(Number(v))}
            formatter={(value) => [`${Math.round(Number(value))}${unit}`, title] as [string, string]}
            contentStyle={{
              background: '#fff',
              border: '1px solid #e5e5e5',
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={stroke}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatHour(t: number): string {
  // Axis tick: the zone label lives on the tooltip (fmtDateTime), not on
  // every tick.
  return fmtTime(t).replace(/ \S+$/, '');
}
